import { Button, Box, styled, Select, MenuItem, Typography, Grid2 } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import WishlistCollectionCard, {
  WishlistCollectionCardProps,
} from "../../../../Components/Cards/WishlistCollectionCard/wishlist-collection-card";
import FiltersList from "../../../../Components/Filters/FiltersList/filters-list";
import SearchIcon from "@mui/icons-material/Search";
import EmptyListSlot from "../../../../Components/Misc/EmptyListSlot";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import { SearchInput } from "../../../../Components/Misc/SearchExpand/search-expand";
import ToggleLayout from "../../../../Components/Misc/ToggleLayout";
import BrowsingSubbar from "../../../../Components/Subbars/BrowsingSubbar";
import DashboardLayout from "../../../../Layouts/Dashboard/dashboard";
import { handleSelectClickNumber, isItemSelectedNumber } from "../../../../Utils/Helpers/selectMode";
import AddIcon from "@mui/icons-material/Add";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { assetService, cacheService, thumbnailService } from "../../../../Core/Api/services";
import { DbObjectCollectionInfos } from "../../../../Core/Api/Types/db-object-collection-infos";
import { ThumbnailSize } from "../../../../Core/Api/Thumbnail/thumbnail-api-service";
import SelectModeSubbarOpener from "../../../../Components/Banners/SelectModeBanner/opener";
import { DbUserInfos } from "../../../../Core/Api/Types/db-user-infos";
import { GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import moment from "moment";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import useCollectionActions from "../../../../Utils/Hooks/useCollectionActions";
import CollectionBanner from "../../../../Components/Banners/CollectionBanner/collection-banner";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { MetadataFieldValueObjectTypes } from "../../../../Core/Api/Types/metadata-field-value-object-types";
import { CollectionType } from "../../../../Core/Api/Types/db-collection-type";
import { CollectionLocation } from "../../../../Core/Api/Types/db-collection-location";
import RequiredIndicator from "../../../../Components/Misc/RequiredIndicator";
import { useTranslation } from "react-i18next";

const Collection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const collectionModal = useCollectionActions();
  const { id: userId, groupId } = { ...(cacheService.GetUserInfo() as DbUserInfos) };
  const [layout, SetLayout] = useState<"grid" | "list">("grid");
  const [sort, SetSort] = useState<string>("");
  const [locationFilter, setLocationFilter] = useState<string>("");
  const [publishFilter, setPublishFilter] = useState<string>("");
  const [sortSearch, SetSortSearch] = useState<string>("");
  const [selectedCollections, setSelectedCollections] = useState<number[]>([]);
  const [wishlistData, SetWishlistData] = useState<DbObjectCollectionInfos[]>([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const isEmpty = wishlistData?.length === 0;

  const data: {
    list: Omit<WishlistCollectionCardProps, "selectMode" | "index">[];
  } = React.useMemo(
    () => ({
      list: wishlistData.map((collection) => {
        let covers: string[] = [];

        if (collection.objectInfos)
          covers =
            collection.objectInfos.length > 4
              ? collection.objectInfos
                  ?.slice(0, 3)
                  .map<string>((obj) =>
                    thumbnailService.GetObjectPosterUrl(
                      obj.layer === ObjectLayer.Title ? obj.id : obj.parentTitleId!,
                      ThumbnailSize.small
                    )
                  )
              : collection.objectInfos.map<string>((obj) =>
                  thumbnailService.GetObjectPosterUrl(
                    obj.layer === ObjectLayer.Title ? obj.id : obj.parentTitleId!,
                    ThumbnailSize.small
                  )
                ) || [];

        return {
          isCreationCard: false,
          id: collection.id,
          title: collection.name!,
          nbTitles: collection.objectInfos?.filter((x) => x.layer === ObjectLayer.Title).length || 0,
          nbTotaleItem: collection.objectIds?.length || 0,
          covers: [covers[0] || "", covers[1] || "", covers[2] || "", covers[3] || ""],
          selectMode: true,
          creationInfos: collection.creationInfos,
          hidden: !collection.targetGroupIds?.includes(groupId!) && collection.targetUserIds?.includes(userId!),
          location: collection.location || CollectionLocation.List,
          isPublished: collection.isPublished || false,
        };
      }),
    }),
    [wishlistData]
  );

  const onLayoutChange = (e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => {
    SetLayout(value);
  };

  const listToRender = useMemo<Omit<WishlistCollectionCardProps, "selectMode" | "index">[]>(() => {
    let list = data.list.filter((x) => x.title.includesAllWords(sortSearch));
    if (sort === "name") list.sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "date") list.sort((a, b) => a.id - b.id); // Short cut as older ids are newer re-used
    return list;
  }, [sortSearch, sort, data]);

  const wishlistAction: GridColDef[] = React.useMemo(
    () => [
      {
        field: "title",
        headerName: t("table:headerName.collectionName"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
      },
      {
        field: "nbTotaleItem",
        headerName: t("common:elements"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
      },
      {
        field: "",
        headerName: t("common:createdAtLowerCase"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (value: any) => (
          <>{moment(value.row.creationInfos.date).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS).toString()}</>
        ),
      },
      {
        field: "hidden",
        headerName: t("table:headerName.visibility"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (value: any) =>
          value ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon color="success" />,
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        align: "right",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<DeleteOutlineIcon sx={{ width: 20, height: 20, color: "error.main" }} />}
            label={t("common:tbd")}
            onClick={() => {
              assetService.DeleteCollection(params?.row?.id).then((data) => {
                if (data.success !== true) return;

                SetWishlistData(wishlistData.filter((x) => x.id !== params?.row?.id));
              });
            }}
          />,
        ],
      },
    ],
    [wishlistData]
  );

  React.useEffect(() => {
    assetService.GetCollectionStoreFront().then((data) => {
      SetWishlistData(data);
      setLoadingCollections(false);
    });
  }, []);

  return (
    <DashboardLayout
      sxContent={{ px: 2 }}
      Subbar={
        <BrowsingSubbar
          title={t("common:movies")}
          start={<FiltersList layer={ObjectLayer.Title} type={MetadataFieldValueObjectTypes.DbAssetObjectInfos} />}
          end={
            <>
              <SearchInput
                value={sortSearch}
                onChange={(value) => SetSortSearch(value.target.value)}
                label={t("common:standard")}
                placeholder={t("common:placeholder")}
                endAdornment={<SearchIcon htmlColor="text.primary" />}
              />
              <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={sort}
                onChange={(value) => SetSort(value.target.value)}
              >
                <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
                <MenuItem value="name">{t("common:title")}</MenuItem>
                <MenuItem value="date">{t("common:recent")}</MenuItem>
              </Select>
              <ToggleLayout value={layout} onChange={onLayoutChange} />
            </>
          }
        />
      }
      Banner={
        <CollectionBanner
          collections={wishlistData.filter((x) => selectedCollections.includes(x.id))}
          handleCloseClick={() => setSelectedCollections([])}
          removePostAction={() => SetWishlistData(wishlistData.filter((x) => !selectedCollections.includes(x.id)))}
        ></CollectionBanner>
      }
    >
      <ListPageHeader mb={3}>
        {/* <ListPageHeaderLeft title="My storefront collections" /> */}
        <ListPageHeaderLeft>
          {listToRender.length > 0 && (
            <Typography variant="h5" color="text.primary">
              {t("component:listPageHeaderLeft.storefrontCollection", { count: listToRender.length })}
            </Typography>
          )}
        </ListPageHeaderLeft>
        <ListPageHeaderRight>
          <>
            <SearchInput
              value={sortSearch}
              onChange={(value) => SetSortSearch(value.target.value)}
              label={t("common:standard")}
              placeholder={t("common:placeholder")}
              endAdornment={<SearchIcon htmlColor="text.primary" />}
            />
            <Select
              displayEmpty
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              value={sort}
              onChange={(value) => SetSort(value.target.value)}
            >
              <MenuItem value="">{t("menu:item.sortBy")}</MenuItem>
              <MenuItem value="name">{t("common:title")}</MenuItem>
              <MenuItem value="date">{t("common:recent")}</MenuItem>
            </Select>
            <Select
              displayEmpty
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              value={locationFilter}
              onChange={(value) => setLocationFilter(value.target.value)}
            >
              <MenuItem value="">{t("common:location")}</MenuItem>
              <MenuItem value="carousel">
                {t("menu:item.carousel")} <RequiredIndicator color={"success.main"} sx={{ ml: 0.5 }} />
              </MenuItem>
              <MenuItem value="others">{t("common:others")}</MenuItem>
            </Select>
            <Select
              displayEmpty
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              value={publishFilter}
              onChange={(value) => setPublishFilter(value.target.value)}
            >
              <MenuItem value="">{t("menu:item.published")}</MenuItem>
              <MenuItem value="yes">{t("common:yes")}</MenuItem>
              <MenuItem value="no">{t("common:no")}</MenuItem>
            </Select>
            <ToggleLayout value={layout} onChange={onLayoutChange} />
          </>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => collectionModal.CreateNewCollectionModal(CollectionType.Storefront)}
          >
            {t("button:newStorefrontCollection")}
          </Button>
        </ListPageHeaderRight>
      </ListPageHeader>
      {loadingCollections && <TableSkeleton />}
      {!loadingCollections &&
        (isEmpty ? (
          <EmptyListSlot
            title={t("component:emptyListSlot.title.itIsEmptyHere")}
            desc={t("component:emptyListSlot.desc.noStorefrontCollectionsCreated")}
            icon={<CollectionsBookmarkOutlinedIcon />}
            CTA={
              <Button
                variant="contained"
                onClick={() => collectionModal.CreateNewCollectionModal(CollectionType.Storefront)}
              >
                {t("button:createFirstStorefrontCollection")}
              </Button>
            }
          />
        ) : layout === "grid" ? (
          <Grid2 container columns={24}>
            {listToRender
              .filter(
                (x) =>
                  (locationFilter === "" ||
                    (locationFilter === "carousel" && x.location === CollectionLocation.Carousel) ||
                    (locationFilter === "others" && x.location !== CollectionLocation.Carousel)) &&
                  (publishFilter === "" ||
                    (publishFilter === "yes" && x.isPublished) ||
                    (publishFilter === "no" && !x.isPublished))
              )
              .map((collection, index) => (
                <Grid2 size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={collection.id}>
                  <WishlistCollectionCard
                    selectedOnClick={selectedCollections.length !== 0}
                    selected={isItemSelectedNumber(collection.id, selectedCollections)}
                    onSelectClick={handleSelectClickNumber(selectedCollections, setSelectedCollections)}
                    index={collection.id}
                    isCarousel={collection.location === CollectionLocation.Carousel}
                    {...collection}
                    selectMode
                  />
                </Grid2>
              ))}
          </Grid2>
        ) : (
          <DataTable
            noControls
            rows={
              listToRender.filter(
                (x) =>
                  (locationFilter === "" ||
                    (locationFilter === "carousel" && x.location === CollectionLocation.Carousel) ||
                    (locationFilter === "others" && x.location !== CollectionLocation.Carousel)) &&
                  (publishFilter === "" ||
                    (publishFilter === "yes" && x.isPublished) ||
                    (publishFilter === "no" && !x.isPublished))
              ) || []
            }
            columnHeaderHeight={48}
            rowHeight={56}
            checkboxSelection={false}
            disableRowSelectionOnClick
            onRowClick={(data: any) => navigate(`${data.id}`)}
            columns={wishlistAction}
            pagination={true}
            selectedRows={selectedCollections}
            setSelectedRows={setSelectedCollections}
          />
        ))}
      <SelectModeSubbarOpener selectedObjects={selectedCollections} />
    </DashboardLayout>
  );
};

export default Collection;

const CardGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(4),
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
  },
}));
